// hooks/useInactivityLogout.ts
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { useLogout } from './api/useLogout';

const INACTIVITY_TIMEOUT =
  (parseInt(process.env.REACT_APP_AUTH_IN_ACTIVITY_MIN || '90') as number) * 60 * 1000;
const ACTIVITY_KEY = 'lastActivityTime';

export const useInactivityLogout = () => {
  const { isAuthenticated } = useSelector((state: RootState) => state.user);
  const { logout } = useLogout();
  const lastActivityUpdate = useRef(Date.now());

  useEffect(() => {
    if (!isAuthenticated) return;

    // Set initial activity time when they log in
    localStorage.setItem(ACTIVITY_KEY, Date.now().toString());

    const checkInactivity = () => {
      const storedActivityTime = parseInt(localStorage.getItem(ACTIVITY_KEY) || '0', 10);
      const now = Date.now();
      const timeLeft = storedActivityTime + INACTIVITY_TIMEOUT - now;

      if (timeLeft <= 0) {
        logout(); // Only interact with Redux/API when the user is actually logging out
      }
    };

    // Check every 10 seconds
    const interval = setInterval(checkInactivity, 10000);

    // Throttle local storage writes to max once every 5 seconds
    const resetTimer = () => {
      const now = Date.now();
      if (now - lastActivityUpdate.current > 5000) {
        lastActivityUpdate.current = now;
        localStorage.setItem(ACTIVITY_KEY, now.toString()); // Updates for all tabs instantly!
      }
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((event) => {
      window.addEventListener(event, resetTimer, { passive: true });
    });

    return () => {
      clearInterval(interval);
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [isAuthenticated, logout]);
};
